@if (open()) {
  <div class="editor-backdrop" (click)="save()"></div>

  <div class="editor-panel" role="dialog" [attr.aria-label]="'editor.title' | t">
    <header class="editor-header">
      <span class="mat-sym header-icon">edit</span>
      <h3>{{ cell() }}</h3>
      <button class="editor-close" type="button" (click)="save()">
        <span class="mat-sym">close</span>
      </button>
    </header>

    <div class="editor-body">
      <!-- image editor area -->
      <div class="image-section">
        <div
          class="image-canvas"
          [class.flat-top]="isFlat"
          #imageCanvas
          (dragover)="onDragOver($event)"
          (drop)="onImageDrop($event)"
        >
          @if (!hasImage()) {
            <div class="image-placeholder">
              <span class="mat-sym placeholder-icon">image</span>
              <span>{{ 'editor.drop-image' | t }}</span>
            </div>
          }
          <div class="hex-frame"></div>
          @if (!hideText()) {
            <div class="editor-label">{{ cell() }}</div>
          }
        </div>

        <div class="image-controls">
          <button type="button" class="link-btn" [class.linked]="isLinked" (click)="toggleLink()" [attr.title]="(isLinked ? 'editor.unlink-transforms' : 'editor.link-transforms') | t">
            <span class="link-icon" [class.broken]="!isLinked"></span>
          </button>
          <button type="button" class="upload-btn" (click)="fileInput.click()">
            <span class="mat-sym">upload</span> {{ 'editor.upload' | t }}
          </button>
          <button type="button" class="camera-btn" (click)="openCamera()">
            <span class="mat-sym">photo_camera</span> {{ 'editor.camera' | t }}
          </button>
          <button type="button" class="orientation-btn" (click)="toggleOrientation()" [attr.title]="(isFlat ? 'editor.switch-to-point' : 'editor.switch-to-flat') | t">
            <span class="mat-sym">crop_rotate</span> {{ (isFlat ? 'editor.flat-top' : 'editor.point-top') | t }}
          </button>
          <input #fileInput type="file" name="tile-image" accept="image/*" hidden (change)="onFileSelect($event)" />
          <input #cameraFallbackInput type="file" accept="image/*" capture="environment" hidden (change)="onFileSelect($event)" />
        </div>
      </div>

      <!-- property fields -->
      <div class="fields">
        <label class="field-row">
          <span class="field-label"><span class="mat-sym">link</span> {{ 'editor.link' | t }}</span>
          <input
            class="field-input link-input"
            [class.link-denied]="linkDenied"
            [class.link-warned]="linkWarned"
            type="text"
            name="tile-link"
            placeholder="https://..."
            [ngModel]="linkValue"
            (ngModelChange)="linkValue = $event"
            (blur)="onLinkBlur()"
            [title]="linkSafetyReason"
          />
        </label>

        <div class="color-row">
          <label class="color-control">
            <span class="color-btn">
              <span class="mat-sym">palette</span> {{ 'editor.bg' | t }}
            </span>
            <input
              class="field-input color-text"
              type="text"
              name="tile-background-color-text"
              placeholder="#1E1E1E"
              [ngModel]="backgroundColorValue"
              (ngModelChange)="backgroundColorValue = $event; onBackgroundColorChange($event)"
            />
            <input
              class="color-picker"
              type="color"
              name="tile-background-color"
              [ngModel]="backgroundColorValue || '#1E1E1E'"
              (ngModelChange)="backgroundColorValue = $event; onBackgroundColorChange($event)"
            />
          </label>

          <label class="color-control">
            <span class="color-btn">
              <span class="mat-sym">border_color</span> {{ 'editor.border' | t }}
            </span>
            <input
              class="field-input color-text"
              type="text"
              name="tile-border-color-text"
              placeholder="#000000"
              [ngModel]="borderColorValue"
              (ngModelChange)="borderColorValue = $event; onBorderColorChange($event)"
            />
            <input
              class="color-picker"
              type="color"
              name="tile-border-color"
              [ngModel]="borderColorValue || '#000000'"
              (ngModelChange)="borderColorValue = $event; onBorderColorChange($event)"
            />
          </label>
        </div>

      </div>

      <!-- Q&A panel: lists [Q]-prefixed notes on the cell with inline
           answer inputs. A submitted answer commits as a sibling
           [A:<qId>] note; the panel refreshes on a short delay. -->
      @if (qaItems.length > 0) {
        <div class="qa-section">
          <header class="qa-header">
            <span class="mat-sym qa-icon">quiz</span>
            <h4>{{ 'editor.qa.title' | t }}</h4>
          </header>
          @for (item of qaItems; track item.qId) {
            <div class="qa-item" [class.qa-answered]="item.answer">
              <p class="qa-question">{{ item.question }}</p>
              @if (item.answer) {
                <p class="qa-answer">{{ item.answer }}</p>
              } @else {
                <div class="qa-answer-row">
                  <textarea
                    class="field-input qa-answer-input"
                    name="qa-answer-{{ item.qId }}"
                    rows="2"
                    [placeholder]="'editor.qa.answer-placeholder' | t"
                    [(ngModel)]="qaAnswerDraft[item.qId]"
                  ></textarea>
                  <button
                    type="button"
                    class="qa-submit"
                    (click)="submitAnswer(item.qId)"
                  >{{ 'editor.qa.done' | t }}</button>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <footer class="editor-footer">
      <div class="footer-tools">
        <button type="button" class="tool-btn" [title]="'editor.search-google' | t" (click)="searchGoogle()">
          <span class="mat-sym">search</span>
        </button>
        <button type="button" class="tool-btn" [class.active]="!hideText()" [title]="(hideText() ? 'editor.show-text' : 'editor.hide-text') | t" (click)="toggleHideText()">
          <span class="mat-sym" [class.filled]="!hideText()">{{ hideText() ? 'visibility_off' : 'text_fields' }}</span>
        </button>
      </div>
      <div class="footer-actions">
        <button type="button" (click)="cancel()">{{ 'editor.cancel' | t }}</button>
        <button type="button" class="primary" (click)="save()">{{ 'editor.save' | t }}</button>
      </div>
    </footer>
  </div>

  @if (cameraActive) {
    <div class="camera-overlay">
      <video #cameraVideo autoplay playsinline></video>
      <div class="camera-hex-frame" [class.flat-top]="cameraFlat"></div>
      <div class="camera-controls">
        <button type="button" class="camera-control-btn" (click)="closeCamera()">
          <span class="mat-sym">close</span>
        </button>
        <button type="button" class="camera-shutter" (click)="capturePhoto()"></button>
        <button type="button" class="camera-control-btn" (click)="toggleCameraOrientation()">
          <span class="mat-sym">crop_rotate</span>
        </button>
      </div>
    </div>
  }
}
